import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import PublicLayout from '../components/public/PublicLayout';
import {
  Building2, ArrowLeft, ChevronRight, Coins, Clock, Calendar,
  GraduationCap, MapPin, Award, BookOpen, Sparkles, Play,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map a degree-type string ("BEng", "MSc", "Bachelor of Engineering"…) to a
// program level the student understands at a glance.
const degreeProgramOf = (raw) => {
  const v = String(raw || '').toLowerCase().trim();
  if (!v) return 'Not specified';
  if (/(phd|doctor|d\.phil|dphil)/.test(v)) return 'Doctorate';
  if (/(master|post.*grad|postgraduate|msc|m\.sc|^ma\b|\bma\b|m\.a|mba|meng|m\.eng|mres|mphil|mph|llm|pgdip|pg.*cert)/.test(v)) return 'Postgraduate';
  if (/(bachelor|under.*grad|undergraduate|b\.sc|bsc|b\.a|^ba\b|\bba\b|b\.eng|beng|b\.tech|btech|bba|bcom|llb|mbbs|foundation|diploma|hnd|hnc|associate)/.test(v)) return 'Undergraduate';
  return 'Other';
};

// Sections present in the detail page (used by jump-to-section sidebar)
const SECTIONS = [
  { id: 'overview', label: 'Program Overview', icon: BookOpen, key: 'program_overview' },
  { id: 'admission', label: 'Admission Requirements', icon: GraduationCap, key: 'admission_requirements' },
  { id: 'eligibility', label: 'Eligibility', icon: Award, key: 'eligibility' },
  { id: 'fees', label: 'Fees & Funding', icon: Coins, key: 'fees_funding' },
];

const StatChip = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 min-w-[150px]">
    <div className="w-10 h-10 rounded-full bg-[#F5A623] text-white flex items-center justify-center shrink-0">
      {Icon && <Icon className="w-4 h-4" />}
    </div>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider font-bold text-[#1A2A4F]/70">{label}</p>
      <p className="text-sm font-bold text-[#1A2A4F] truncate">{value || '—'}</p>
    </div>
  </div>
);

const Section = ({ id, title, icon: Icon, children }) => (
  <section id={id} className="scroll-mt-32 bg-white border border-gray-100 rounded-2xl p-6 sm:p-8" data-testid={`section-${id}`}>
    <div className="flex items-center gap-2 mb-3">
      <span className="w-6 h-6 rounded bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center">
        <Play className="w-3.5 h-3.5 fill-[#F5A623]" />
      </span>
      <h2 className="text-lg sm:text-xl font-bold text-[#1A2A4F]">{title}</h2>
      {Icon && <Icon className="w-4 h-4 text-gray-300 hidden sm:block" />}
    </div>
    <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{children}</div>
  </section>
);

const CourseDetailPage = () => {
  const { slug } = useParams();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState('overview');

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/public/program-by-slug/${slug}`);
        setProgram(r.data);
      } catch { setNotFound(true); }
      setLoading(false);
    };
    run();
  }, [slug]);

  // Scroll-spy: highlight the visible section in the sidebar
  useEffect(() => {
    if (!program) return;
    const ids = SECTIONS.map((s) => s.id);
    const onScroll = () => {
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top < 200 && rect.bottom > 200) {
          setActiveAnchor(id);
          return;
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [program]);

  const m = useMemo(() => program?.metadata || {}, [program]);
  const heroSrc = m.hero_image || null;

  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-5xl mx-auto px-4 py-20 text-center text-gray-500" data-testid="course-detail-loading">
          Loading program…
        </div>
      </PublicLayout>
    );
  }
  if (notFound || !program) {
    return (
      <PublicLayout>
        <div className="max-w-5xl mx-auto px-4 py-20 text-center" data-testid="course-detail-not-found">
          <h1 className="text-2xl font-bold text-[#1A2A4F]">Program not found</h1>
          <Link to="/courses" className="mt-6 inline-flex items-center gap-1 text-[#F5A623] font-semibold hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to all courses
          </Link>
        </div>
      </PublicLayout>
    );
  }

  const minFee = (() => {
    const br = m.tuition_breakdown || [];
    const nums = br.map((b) => b?.amount).filter((n) => typeof n === 'number');
    return nums.length ? Math.min(...nums) : null;
  })();

  return (
    <PublicLayout>
      {/* HERO */}
      <div className="relative w-full h-56 sm:h-72 lg:h-[340px] bg-[#1A2A4F] overflow-hidden" data-testid="program-hero">
        {heroSrc ? (
          <img src={heroSrc} alt={m.university_name || program.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#1A2A4F] via-[#243a6a] to-[#1A2A4F] flex items-center justify-center">
            <div className="text-center text-white">
              <Building2 className="w-12 h-12 mx-auto opacity-50" />
              <p className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-wide uppercase opacity-90">{m.university_name}</p>
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
      </div>

      <div className="max-w-6xl mx-auto px-4 -mt-8">
        {/* Back button */}
        <Link to="/courses"
          className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#1A2A4F] text-white shadow hover:brightness-95"
          data-testid="back-to-courses"
          aria-label="Back">
          <ArrowLeft className="w-4 h-4" />
        </Link>

        {/* Title card */}
        <div className="mt-4 bg-white border border-gray-100 rounded-2xl p-6 sm:p-7 shadow-sm">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1A2A4F]" data-testid="program-title">
            Program Details — {m.course_title || program.name}
          </h1>
          <div className="mt-2 flex items-center gap-2 text-sm text-gray-700">
            <Building2 className="w-4 h-4 text-[#1A2A4F]" />
            <span className="font-semibold">{m.university_name}</span>
          </div>
          {m.faculty && (
            <p className="mt-1 text-sm text-gray-700"><span className="font-semibold">Faculty:</span> {m.faculty}</p>
          )}

          {/* Stats strip */}
          <div className="mt-5 rounded-xl bg-[#F5A623]/10 border border-[#F5A623]/30 p-4 sm:p-5">
            <div className="flex flex-wrap gap-5 sm:gap-7">
              <StatChip icon={Coins} label="Annual Fee"
                value={minFee != null ? minFee.toLocaleString() : (m.annual_tuition_fees_raw || '—')} />
              <StatChip icon={Coins} label="Currency" value={m.currency} />
              <StatChip icon={Clock} label="Duration" value={m.duration} />
              <StatChip icon={Calendar} label="Intake" value={m.intake || m.session_start} />
              <StatChip icon={GraduationCap} label="Degree Type" value={m.degree_type || m.degree} />
              <StatChip icon={Award} label="Degree Program" value={degreeProgramOf(m.degree_type || m.degree || m.course_level)} />
              <StatChip icon={MapPin} label="Delivery Method" value={m.delivery_method} />
            </div>
          </div>

          {/* Apply CTA */}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link to={`/apply/${program.id}`}
              className="inline-flex items-center gap-2 bg-[#F5A623] text-white font-bold px-6 py-3 rounded-md hover:brightness-95"
              data-testid="start-application-btn">
              Start Application <ChevronRight className="w-4 h-4" />
            </Link>
            {Array.isArray(m.search_keywords) && m.search_keywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {m.search_keywords.slice(0, 4).map((k, i) => (
                  <span key={i} className="inline-block bg-[#1A2A4F]/5 text-[#1A2A4F] text-[11px] font-semibold px-2 py-1 rounded">{k}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        {/* LEFT: sections */}
        <div className="space-y-5">
          {SECTIONS.map((s) => {
            const txt = m[s.key];
            const fallback = s.id === 'overview'
              ? 'No program overview has been added yet. Detailed content (curriculum, modules, structure) will be updated when available.'
              : s.id === 'admission'
              ? 'No specific admission requirements listed. Detailed eligibility criteria (such as prior degree, GPA, English language proficiency, references, etc.) will be updated when available.'
              : s.id === 'eligibility'
              ? 'Eligibility details will be added soon.'
              : 'See institution for the latest fees & funding information.';
            return (
              <Section key={s.id} id={s.id} title={s.label} icon={s.icon}>
                {txt || fallback}
              </Section>
            );
          })}

          {Array.isArray(m.scholarship) && m.scholarship.length > 0 && (
            <Section id="scholarships" title="Scholarships available" icon={Award}>
              <ul className="list-disc ml-5 space-y-1">
                {m.scholarship.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </Section>
          )}
        </div>

        {/* RIGHT: jump-to-section sticky */}
        <aside className="lg:sticky lg:top-32 h-fit" data-testid="jump-to-section">
          <div className="bg-white border border-gray-100 rounded-2xl p-5">
            <p className="text-center text-base font-bold text-[#1A2A4F] mb-2">Jump to Section</p>
            <div className="h-px bg-gray-100 -mx-5 mb-3" />
            <ul className="space-y-1">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById(s.id);
                      if (el) {
                        window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 100, behavior: 'smooth' });
                        setActiveAnchor(s.id);
                      }
                    }}
                    className={`block text-center py-2.5 text-sm rounded transition ${
                      activeAnchor === s.id
                        ? 'bg-[#F5A623]/10 text-[#F5A623] font-bold'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-[#1A2A4F]'
                    }`}
                    data-testid={`jump-${s.id}`}
                  >
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <Link to={`/apply/${program.id}`}
                className="block text-center bg-[#1A2A4F] text-white text-sm font-bold py-2.5 rounded-md hover:brightness-95"
                data-testid="sidebar-apply-btn">
                Start Application
              </Link>
            </div>
          </div>

          <div className="mt-4 bg-[#F5A623]/10 border border-[#F5A623]/30 rounded-2xl p-5 text-center">
            <Sparkles className="w-6 h-6 mx-auto text-[#F5A623]" />
            <p className="mt-2 text-sm font-bold text-[#1A2A4F]">Need guidance?</p>
            <p className="text-xs text-gray-700 mt-1">Speak to a counsellor about this program.</p>
            <Link to="/contact" className="mt-3 inline-block text-xs font-bold text-[#F5A623] hover:underline">
              Contact a counsellor →
            </Link>
          </div>
        </aside>
      </div>
    </PublicLayout>
  );
};

export default CourseDetailPage;
